import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import moment from 'moment';
import { date_format, datetime_format } from 'src/date/date.configuration';
import {
  IEHDSDischargeReport,
  IEHDSDischargeReportHeader,
} from 'src/app/interfaces/DischargeReport/IEHDSDischargeReport';
import {
  IDischargeReportPreviewState,
  IIssuedDischargeReportIndicator,
} from 'src/app/interfaces/DischargeReport/IDischargeReportPreview';
import {
  IEHDSCodeableConcept,
  IEHDSHealthProfessional,
  IEHDSHumanName,
  IEHDSIdentifier,
  IEHDSOrganisation,
} from 'src/app/interfaces/DischargeReport/IEHDSCommon';

@Component({
  selector: 'app-discharge-report-header',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzIconModule],
  templateUrl: './discharge-report-header.component.html',
  styleUrls: ['./discharge-report-header.component.css'],
})
export class DischargeReportHeaderComponent {
  @Input({ required: true }) report!: IEHDSDischargeReport;
  @Input({ required: true }) previewState!: IDischargeReportPreviewState;
  @Input({ required: true }) issuedReport!: IIssuedDischargeReportIndicator;

  get patientName(): string | undefined {
    return this.humanNameText(this.report.header.subject?.name?.[0]);
  }

  get authorName(): string | undefined {
    return this.authorText(this.report.header);
  }

  get documentStatusText(): string | undefined {
    return this.codeableConceptText(this.report.header.status);
  }

  formatDate(value?: string): string {
    return value ? moment(value).format(date_format) : '';
  }

  formatDateTime(value?: string): string {
    return value ? moment(value).format(datetime_format) : '';
  }

  private codeableConceptText(
    concept?: IEHDSCodeableConcept
  ): string | undefined {
    if (!concept) {
      return undefined;
    }

    if (concept.text) {
      return concept.text;
    }

    const coding = concept.coding?.[0];
    return coding?.display || coding?.code || undefined;
  }

  private humanNameText(name?: IEHDSHumanName): string | undefined {
    if (!name) {
      return undefined;
    }

    if (name.text) {
      return name.text;
    }

    const parts = [...(name.given ?? []), ...(name.family ?? [])];
    return parts.length ? parts.join(' ') : undefined;
  }

  private identifierText(
    identifiers?: IEHDSIdentifier[]
  ): string | undefined {
    return identifiers?.[0]?.value;
  }

  private healthProfessionalText(
    professional?: IEHDSHealthProfessional
  ): string | undefined {
    return (
      this.humanNameText(professional?.name) ||
      this.identifierText(professional?.identifier)
    );
  }

  private organisationText(
    organisation?: IEHDSOrganisation
  ): string | undefined {
    return organisation?.name || this.identifierText(organisation?.identifier);
  }

  private authorText(
    header?: IEHDSDischargeReportHeader
  ): string | undefined {
    if (!header) {
      return undefined;
    }

    const professional = header.authorEHDSHealthProfessional?.[0];
    if (professional) {
      return this.healthProfessionalText(professional);
    }

    const organisation = header.authorEHDSOrganisation?.[0];
    if (organisation) {
      return this.organisationText(organisation);
    }

    return undefined;
  }
}